import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Save, ChevronDown, ChevronUp, Phone } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  {
    key: "warning_signs",
    title: "1. Warning signs",
    desc: "Thoughts, feelings, behaviours or situations that tell you a crisis may be developing.",
    type: "list",
    placeholder: "e.g. Not sleeping, increased anxiety",
  },
  {
    key: "coping_strategies",
    title: "2. Internal coping strategies (distraction)",
    desc: "Things you can do alone to take your mind off how you feel.",
    type: "list",
    placeholder: "e.g. Going for a walk, gardening, gaming",
  },
  {
    key: "calming_activities",
    title: "3. Calming activities",
    desc: "Activities that soothe your body and mind.",
    type: "list",
    placeholder: "e.g. Box breathing, meditation, hot bath",
  },
  {
    key: "social_distractions",
    title: "4. People & places for distraction",
    desc: "People or places that help you take your mind off things, without discussing problems.",
    type: "contacts",
  },
  {
    key: "trusted_supports",
    title: "5. People I trust to talk to",
    desc: "People you can reach out to and talk openly with about how you feel.",
    type: "contacts",
  },
  {
    key: "professional_supports",
    title: "6. Professional support",
    desc: "GP, care coordinator, crisis team, psychiatrist, therapist.",
    type: "contacts",
  },
  {
    key: "reasons_for_living",
    title: "7. Reasons for living",
    desc: "The things and people that matter most. Shown to you when distress is high.",
    type: "list",
    placeholder: "e.g. My son Lucas, my dog, my future studies",
  },
];

const ENV_CHECKLIST = [
  { key: "selfharm_remove_blades", label: "I have moved sharp objects out of reach" },
  { key: "selfharm_handed_to_person", label: "I have handed risky items to someone I trust" },
  { key: "od_lock_meds", label: "My medications are locked away or held by someone else" },
  { key: "od_weekly_dispense", label: "I'm using a weekly blister pack" },
  { key: "ligature_review", label: "I've reviewed and removed identified ligature risks" },
  { key: "substance_remove", label: "I've removed alcohol/drugs that put me at risk" },
];

export default function SafetyPlan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(0);
  const [draftItem, setDraftItem] = useState({}); // per-step draft
  const [draftContact, setDraftContact] = useState({}); // per-step contact draft

  useEffect(() => {
    api.get("/safety-plan").then((r) => setPlan(r.data));
  }, []);

  if (!plan) return <div className="text-slate-500" data-testid="plan-loading">Loading…</div>;

  const addItem = (key) => {
    const v = (draftItem[key] || "").trim();
    if (!v) return;
    setPlan({ ...plan, [key]: [...(plan[key] || []), v] });
    setDraftItem({ ...draftItem, [key]: "" });
  };
  const removeItem = (key, idx) => {
    const arr = [...(plan[key] || [])];
    arr.splice(idx, 1);
    setPlan({ ...plan, [key]: arr });
  };
  const addContact = (key) => {
    const c = draftContact[key] || {};
    if (!c.name || !c.phone) return;
    setPlan({ ...plan, [key]: [...(plan[key] || []), { name: c.name, phone: c.phone, relationship: c.relationship || "", can_message: true }] });
    setDraftContact({ ...draftContact, [key]: { name: "", phone: "", relationship: "" } });
  };
  const removeContact = (key, idx) => {
    const arr = [...(plan[key] || [])];
    arr.splice(idx, 1);
    setPlan({ ...plan, [key]: arr });
  };
  const toggleEnv = (k) => {
    setPlan({ ...plan, environment_safety: { ...(plan.environment_safety || {}), [k]: !plan.environment_safety?.[k] } });
  };

  const save = async () => {
    try {
      await api.put("/safety-plan", plan);
      toast.success("Safety plan saved");
    } catch {
      toast.error("Could not save plan");
    }
  };

  const stepsToRender = user?.mode === "inpatient"
    ? [...STEPS, { key: "inpatient_contacts", title: "Ward staff & inpatient contacts", desc: "Named nurse, nurse in charge, advocacy, PALS.", type: "contacts" }]
    : STEPS;

  return (
    <div className="space-y-6 fade-up" data-testid="plan-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">My Safety Plan</h1>
          <p className="text-slate-600 mt-2 max-w-xl">Built on the evidence-based Barbara Stanley &amp; Gregory K. Brown Safety Planning Intervention. Take it slowly — one step at a time.</p>
        </div>
        <button data-testid="plan-save" onClick={save} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-5 py-2.5 font-semibold flex items-center gap-2">
          <Save className="w-4 h-4" /> Save plan
        </button>
      </header>

      <div className="space-y-3">
        {stepsToRender.map((s, i) => {
          const isOpen = open === i;
          const items = plan[s.key] || [];
          return (
            <div key={s.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`plan-step-${s.key}`}>
              <button
                data-testid={`plan-step-toggle-${s.key}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full text-left p-5 flex items-start justify-between gap-3 hover:bg-slate-50"
              >
                <div>
                  <h2 className="font-display text-lg font-bold text-slate-900">{s.title}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">{s.desc}</p>
                  <div className="text-xs text-teal-700 mt-2 font-medium">{items.length} {items.length === 1 ? "entry" : "entries"}</div>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>

              {isOpen && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-3">
                  {/* existing items */}
                  <ul className="space-y-2">
                    {items.map((it, idx) => (
                      <li key={idx} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                        {s.type === "list" ? (
                          <span className="text-slate-800 text-sm">{it}</span>
                        ) : (
                          <span className="text-slate-800 text-sm flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 text-teal-700" />
                            <strong>{it.name}</strong>
                            <span className="text-slate-500">· {it.phone}</span>
                            {it.relationship && <span className="text-slate-400">· {it.relationship}</span>}
                          </span>
                        )}
                        <button
                          data-testid={`plan-remove-${s.key}-${idx}`}
                          onClick={() => (s.type === "list" ? removeItem(s.key, idx) : removeContact(s.key, idx))}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>

                  {/* add */}
                  {s.type === "list" ? (
                    <div className="flex gap-2">
                      <input
                        data-testid={`plan-input-${s.key}`}
                        value={draftItem[s.key] || ""}
                        onChange={(e) => setDraftItem({ ...draftItem, [s.key]: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && addItem(s.key)}
                        placeholder={s.placeholder}
                        className="flex-1 border border-slate-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      />
                      <button data-testid={`plan-add-${s.key}`} onClick={() => addItem(s.key)} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-3 py-2 flex items-center gap-1 text-sm">
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        data-testid={`plan-contact-name-${s.key}`}
                        value={draftContact[s.key]?.name || ""}
                        onChange={(e) => setDraftContact({ ...draftContact, [s.key]: { ...(draftContact[s.key] || {}), name: e.target.value } })}
                        placeholder="Name"
                        className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                      />
                      <input
                        data-testid={`plan-contact-phone-${s.key}`}
                        value={draftContact[s.key]?.phone || ""}
                        onChange={(e) => setDraftContact({ ...draftContact, [s.key]: { ...(draftContact[s.key] || {}), phone: e.target.value } })}
                        placeholder="Phone"
                        className="border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                      />
                      <div className="flex gap-2">
                        <input
                          data-testid={`plan-contact-rel-${s.key}`}
                          value={draftContact[s.key]?.relationship || ""}
                          onChange={(e) => setDraftContact({ ...draftContact, [s.key]: { ...(draftContact[s.key] || {}), relationship: e.target.value } })}
                          placeholder="Relationship"
                          className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                        />
                        <button data-testid={`plan-add-contact-${s.key}`} onClick={() => addContact(s.key)} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-3 py-2 flex items-center gap-1 text-sm">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Environment safety */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="plan-env-safety">
          <h2 className="font-display text-lg font-bold text-slate-900">Environment safety</h2>
          <p className="text-sm text-slate-500 mt-0.5 mb-4">Reducing access to means is one of the most effective steps in keeping yourself safe.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ENV_CHECKLIST.map((c) => (
              <label key={c.key} className="flex items-start gap-3 bg-slate-50 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  data-testid={`plan-env-${c.key}`}
                  checked={!!plan.environment_safety?.[c.key]}
                  onChange={() => toggleEnv(c.key)}
                  className="mt-0.5 w-4 h-4 rounded text-teal-700 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-800">{c.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="sticky bottom-20 md:bottom-4 z-20">
        <button data-testid="plan-save-bottom" onClick={save} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-xl py-3 font-semibold shadow-lg flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Save my plan
        </button>
      </div>
    </div>
  );
}
